import type { Role } from '@personnel/domain/entities/Role';
import type { Permission } from '@personnel/domain/types/PermissionsConst';
import { PERMISSIONS } from '@personnel/domain/types/PermissionsConst';

/**
 * Permission backend IDs that require credentials (email/password) to be enabled
 *
 * If a role has any of these permissions, the credentials section will be enabled.
 * These permissions require users to have email/password for authentication.
 */
export const PERMISSIONS_THAT_REQUIRE_CREDENTIALS = [
  'pos.loginAccess',
  'payTerminal.loginAccess',
  'backoffice.access',
] as const;

export const ROLES_THAT_REQUIRE_CREDENTIALS_AND_PIN = ['Super Admin'] as const;

const normalizeString = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
const NORMALIZED_ROLES_THAT_REQUIRE_CREDENTIALS_AND_PIN = ROLES_THAT_REQUIRE_CREDENTIALS_AND_PIN.map((name) =>
  normalizeString(name),
);

/**
 * Permission backend IDs that require PIN to be enabled
 *
 * If a role has any of these permissions, the PIN section will be enabled.
 * These permissions specifically require PIN authentication for POS and payment terminals.
 */
export const PERMISSIONS_THAT_REQUIRE_PIN = ['pos.pinAccess', 'payTerminal.terminalAccess'] as const;

/**
 * Gets frontend permission IDs that contain any of the required backend IDs
 *
 * @param requiredBackendIds - Array of backend IDs to search for
 * @returns Array of frontend permission IDs that contain those backend IDs
 */
export const getFrontendIdsForBackendIds = (requiredBackendIds: readonly string[]): string[] => {
  const frontendIds = new Set<string>();

  (Object.values(PERMISSIONS) as Permission[]).forEach((permission) => {
    const hasRequiredBackend = permission.primaryIds.some((backendId: string) =>
      requiredBackendIds.includes(backendId),
    );

    if (hasRequiredBackend) {
      frontendIds.add(permission.frontendId);
    }
  });

  return Array.from(frontendIds);
};

/**
 * Checks if a role has any of the required permissions
 *
 * Pure function that validates if a role contains at least one of the specified
 * permission backend IDs. Matching considers both `primaryIds` and `dependencyIds`
 * so that dependency-only backend IDs (e.g. `backoffice.access`, granted by every
 * backoffice/admin permission) still gate form sections. Used to determine if
 * certain form sections should be enabled.
 *
 * @param role - The role to check (can be undefined)
 * @param requiredPermissions - Array of permission backend IDs to check for
 * @returns True if the role has at least one of the required permissions, false otherwise
 *
 * @example
 * const role = { permissions: [{ primaryIds: ['backoffice.moduleMenu'], dependencyIds: ['backoffice.access'] }] };
 * const hasPermission = hasRoleRequiredPermissions(role, PERMISSIONS_THAT_REQUIRE_CREDENTIALS);
 * // Returns: true
 */
export const hasRoleRequiredPermissions = (role: Role | undefined, requiredPermissions: readonly string[]): boolean => {
  if (!role) {
    return false;
  }

  return (
    role.permissions.some((permission) =>
      [...permission.primaryIds, ...permission.dependencyIds].some((backendId: string) =>
        requiredPermissions.includes(backendId),
      ),
    ) || NORMALIZED_ROLES_THAT_REQUIRE_CREDENTIALS_AND_PIN.includes(role.name)
  );
};
